import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { getAbsoluteUrl } from "shared/lib/utils";

import { HomeV2SectionTopHeader } from "scmp-app/components/home-v2/section-top/header";
import { HomeV2SectionTopWidgetOne } from "scmp-app/components/home-v2/section-top/variant/one";
import { HomeV2SectionTopWidgetTwo } from "scmp-app/components/home-v2/section-top/variant/two";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { sectionTopHomeV2Query$key } from "scmp-app/queries/__generated__/sectionTopHomeV2Query.graphql";

import { Container } from "./styles";

export type Section =
  | "china"
  | "hong-kong"
  | "lifestyle"
  | "people-culture"
  | "style"
  | "twia"
  | "wellness";

type Props = {
  className?: string;
  reference: sectionTopHomeV2Query$key;
  section: Section;
};

export const HomeV2SectionTop: FunctionComponent<Props> = ({ className, reference, section }) => {
  const data = useFragment(
    graphql`
      fragment sectionTopHomeV2Query on Query
      @argumentDefinitions(
        isAsiaEdition: { type: "Boolean!" }
        isHongKongEdition: { type: "Boolean!" }
        excludeSsrContentEntityIds: { type: "[String]" }
      ) {
        chinaSectionV2: section(filter: { entityId: "4" }) {
          name
          subSections {
            items(first: 5) {
              edges {
                ...headerSectionTopHomeV2QueueItemsEdge
              }
            }
          }
          topStories {
            items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
              edges {
                node {
                  ... on Content {
                    entityId
                  }
                }
                ...oneSectionTopWidgetHomeV2Queue
                ...twoSectionTopWidgetHomeV2Queue
              }
            }
          }
          ...headerSectionTopHomeV2Section
        }
        hongKongSectionV2: section(filter: { entityId: "2" }) @skip(if: $isAsiaEdition) {
          name
          subSections {
            items(first: 6) {
              edges {
                ...headerSectionTopHomeV2QueueItemsEdge
              }
            }
          }
          topStories {
            items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
              edges {
                node {
                  ... on Content {
                    entityId
                  }
                }
                ...oneSectionTopWidgetHomeV2Queue
                ...twoSectionTopWidgetHomeV2Queue
              }
            }
          }
          ...headerSectionTopHomeV2Section
        }
        lifestyleSectionV2: section(filter: { entityId: "94" }) {
          name
          subSections {
            items(first: 4) {
              edges {
                ...headerSectionTopHomeV2QueueItemsEdge
              }
            }
          }
          topStories {
            items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
              edges {
                node {
                  ... on Content {
                    entityId
                  }
                }
                ...oneSectionTopWidgetHomeV2Queue
                ...twoSectionTopWidgetHomeV2Queue
              }
            }
          }
          ...headerSectionTopHomeV2Section
        }
        peopleCultureSectionV2: section(filter: { entityId: "318202" })
          @skip(if: $isHongKongEdition) {
          name
          subSections {
            items(first: 5) {
              edges {
                ...headerSectionTopHomeV2QueueItemsEdge
              }
            }
          }
          topStories {
            items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
              edges {
                node {
                  ... on Content {
                    entityId
                  }
                }
                ...oneSectionTopWidgetHomeV2Queue
                ...twoSectionTopWidgetHomeV2Queue
              }
            }
          }
          ...headerSectionTopHomeV2Section
        }
        wellnessSectionV2: section(filter: { entityId: "24" }) @include(if: $isHongKongEdition) {
          name
          subSections {
            items(first: 5) {
              edges {
                ...headerSectionTopHomeV2QueueItemsEdge
              }
            }
          }
          topStories {
            items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
              edges {
                node {
                  ... on Content {
                    entityId
                  }
                }
                ...oneSectionTopWidgetHomeV2Queue
                ...twoSectionTopWidgetHomeV2Queue
              }
            }
          }
          ...headerSectionTopHomeV2Section
        }
        styleSectionV2: section(filter: { entityId: "72" }) {
          name
          subSections {
            items(first: 7) {
              edges {
                ...headerSectionTopHomeV2QueueItemsEdge
              }
            }
          }
          topStories {
            items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
              edges {
                node {
                  ... on Content {
                    entityId
                  }
                }
                ...oneSectionTopWidgetHomeV2Queue
                ...twoSectionTopWidgetHomeV2Queue
              }
            }
          }
          ...headerSectionTopHomeV2Section
        }
        twiaSectionV2: section(filter: { entityId: "323045" }) @include(if: $isAsiaEdition) {
          name
          subSections {
            items(first: 6) {
              edges {
                ...headerSectionTopHomeV2QueueItemsEdge
              }
            }
          }
          topStories {
            items(first: 7, exclude: { entityIds: $excludeSsrContentEntityIds }) {
              edges {
                node {
                  ... on Content {
                    entityId
                  }
                }
                ...oneSectionTopWidgetHomeV2Queue
                ...twoSectionTopWidgetHomeV2Queue
              }
            }
          }
          ...headerSectionTopHomeV2Section
        }
      }
    `,
    reference,
  );

  const { listOfArticles, SectionBlockComponent, sectionContents, sectionEntity, subSections } =
    useMemo(() => {
      switch (section) {
        case "china":
          return {
            listOfArticles: (
              data.chinaSectionV2?.topStories?.items?.edges?.map(item => item.node?.entityId) ?? []
            ).join(","),
            SectionBlockComponent: HomeV2SectionTopWidgetOne,
            sectionContents: data.chinaSectionV2?.topStories?.items?.edges ?? [],
            sectionEntity: data.chinaSectionV2,
            subSections: data.chinaSectionV2?.subSections?.items?.edges ?? [],
          };
        case "hong-kong":
          return {
            listOfArticles: (
              data.hongKongSectionV2?.topStories?.items?.edges?.map(item => item.node?.entityId) ??
              []
            ).join(","),
            SectionBlockComponent: HomeV2SectionTopWidgetOne,
            sectionContents: data.hongKongSectionV2?.topStories?.items?.edges ?? [],
            sectionEntity: data.hongKongSectionV2,
            subSections: data.hongKongSectionV2?.subSections?.items?.edges ?? [],
          };
        case "lifestyle":
          return {
            listOfArticles: (
              data.lifestyleSectionV2?.topStories?.items?.edges?.map(item => item.node?.entityId) ??
              []
            ).join(","),
            SectionBlockComponent: HomeV2SectionTopWidgetTwo,
            sectionContents: data.lifestyleSectionV2?.topStories?.items?.edges ?? [],
            sectionEntity: data.lifestyleSectionV2,
            subSections: data.lifestyleSectionV2?.subSections?.items?.edges ?? [],
          };
        case "people-culture":
          return {
            listOfArticles: (
              data.peopleCultureSectionV2?.topStories?.items?.edges?.map(
                item => item.node?.entityId,
              ) ?? []
            ).join(","),
            SectionBlockComponent: HomeV2SectionTopWidgetTwo,
            sectionContents: data.peopleCultureSectionV2?.topStories?.items?.edges ?? [],
            sectionEntity: data.peopleCultureSectionV2,
            subSections: data.peopleCultureSectionV2?.subSections?.items?.edges ?? [],
          };
        case "style":
          return {
            listOfArticles: (
              data.styleSectionV2?.topStories?.items?.edges?.map(item => item.node?.entityId) ?? []
            ).join(","),
            SectionBlockComponent: HomeV2SectionTopWidgetTwo,
            sectionContents: data.styleSectionV2?.topStories?.items?.edges ?? [],
            sectionEntity: data.styleSectionV2,
            subSections: data.styleSectionV2?.subSections?.items?.edges ?? [],
          };
        case "twia":
          return {
            listOfArticles: (
              data.twiaSectionV2?.topStories?.items?.edges?.map(item => item.node?.entityId) ?? []
            ).join(","),
            SectionBlockComponent: HomeV2SectionTopWidgetOne,
            sectionContents: data.twiaSectionV2?.topStories?.items?.edges ?? [],
            sectionEntity: data.twiaSectionV2,
            subSections: data.twiaSectionV2?.subSections?.items?.edges ?? [],
          };
        case "wellness":
          return {
            listOfArticles: (
              data.wellnessSectionV2?.topStories?.items?.edges?.map(item => item.node?.entityId) ??
              []
            ).join(","),
            SectionBlockComponent: HomeV2SectionTopWidgetTwo,
            sectionContents: data.wellnessSectionV2?.topStories?.items?.edges ?? [],
            sectionEntity: data.wellnessSectionV2,
            subSections: data.wellnessSectionV2?.subSections?.items?.edges ?? [],
          };
      }
    }, [
      data.chinaSectionV2,
      data.hongKongSectionV2,
      data.lifestyleSectionV2,
      data.peopleCultureSectionV2,
      data.wellnessSectionV2,
      data.styleSectionV2,
      data.twiaSectionV2,
      section,
    ]);

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: sectionEntity?.name,
        list_of_articles: listOfArticles,
        page_type: "homepage",
        widget_name: "homepage_section_block",
      },
      subcategory: "widget",
    }),
    [sectionEntity?.name, listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (!sectionEntity) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: `hp_section_${section}`,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <HomeV2SectionTopHeader reference={sectionEntity} subSectionReference={subSections} />
        <SectionBlockComponent
          onClick={(entityId, urlAlias) => {
            sendGA4Tracking({
              action: "click",
              category: "recirculation",
              customized_parameters: {
                article_id: entityId,
                block_section: sectionEntity?.name,
                destination_url: getAbsoluteUrl(urlAlias),
                page_type: "homepage",
                widget_name: "homepage_section_block",
              },
              subcategory: "widget",
            });
          }}
          reference={sectionContents}
        />
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2SectionTop.displayName = "HomeV2SectionTop";
